// -- Constants
const MM_SPECS_COUNT_MS = 1200;
const MM_SPECS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

// -- Functions
function mmSpecsEaseOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function mmSpecsParseCount(value) {
  const text = String(value == null ? '' : value).trim();
  const hasComma = text.indexOf(',') !== -1;
  const numeric = text.replace(/,/g, '');
  const parsed = Number(numeric);

  if (text === '' || !Number.isFinite(parsed)) {
    return null;
  }

  const decimalPart = numeric.split('.')[1];

  return {
    target: parsed,
    hasComma: hasComma,
    decimals: decimalPart ? decimalPart.length : 0,
    original: text,
  };
}

function mmSpecsFormatCount(value, meta) {
  const fixed = value.toFixed(meta.decimals);
  if (!meta.hasComma) return fixed;

  const parts = fixed.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

function mmSpecsAnimateCount(el, meta) {
  if (MM_SPECS_REDUCED_MOTION.matches) {
    el.textContent = meta.original;
    return;
  }

  el.textContent = mmSpecsFormatCount(0, meta);
  const start = performance.now();

  function frame(now) {
    const t = Math.min(1, (now - start) / MM_SPECS_COUNT_MS);
    const current = meta.target * mmSpecsEaseOutCubic(t);
    el.textContent = mmSpecsFormatCount(current, meta);
    if (t < 1) {
      requestAnimationFrame(frame);
    } else {
      el.textContent = meta.original;
    }
  }

  requestAnimationFrame(frame);
}

function mmSpecsSyncPlayButton(button, video) {
  if (!button || !video) return;

  const isPlaying = !video.paused && !video.ended;
  button.classList.toggle('is-playing', isPlaying);
  button.setAttribute('aria-pressed', isPlaying.toString());

  const playLabel = button.getAttribute('data-label-play') || 'Play video';
  const pauseLabel = button.getAttribute('data-label-pause') || 'Pause video';
  button.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
}

// -- Custom element
class MmSpecs extends HTMLElement {
  connectedCallback() {
    this._counts = this.querySelectorAll('[data-mm-count]');
    this._track = this.querySelector('[data-mm-specs-track]');
    this._pages = this.querySelectorAll('[data-mm-specs-page]');
    this._dots = this.querySelectorAll('[data-mm-specs-dot]');
    this._videoCards = this.querySelectorAll('[data-mm-specs-video]');
    this._modal = this.querySelector('[data-mm-specs-modal]');
    this._modalContent = this.querySelector('[data-mm-specs-modal-content]');
    this._modalCloses = this.querySelectorAll('[data-mm-specs-modal-close]');
    this._cleanups = [];
    this._lastFocusedCard = null;

    this._onTrackScroll = this._onTrackScroll.bind(this);
    this._onDotClick = this._onDotClick.bind(this);
    this._onDocumentKeyDown = this._onDocumentKeyDown.bind(this);

    this._bindCounts();
    this._bindSlider();
    this._bindVideos();
    this._bindModal();
  }

  disconnectedCallback() {
    if (this._countObserver) {
      this._countObserver.disconnect();
      this._countObserver = null;
    }
    if (this._track) {
      this._track.removeEventListener('scroll', this._onTrackScroll);
    }
    this._dots.forEach((dot) => {
      dot.removeEventListener('click', this._onDotClick);
    });
    if (this._cleanups) {
      this._cleanups.forEach((cleanup) => cleanup());
      this._cleanups = [];
    }
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('mm-specs-modal-open');
    }
  }

  _bindCounts() {
    if (!this._counts.length) return;

    this._countObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.getAttribute('data-mm-counted') === 'true') return;

          const meta = mmSpecsParseCount(el.getAttribute('data-mm-count') || el.textContent);
          if (!meta) return;

          el.setAttribute('data-mm-counted', 'true');
          this._countObserver.unobserve(el);
          mmSpecsAnimateCount(el, meta);
        });
      },
      { threshold: 0.4 }
    );

    this._counts.forEach((el) => this._countObserver.observe(el));
  }

  _bindSlider() {
    if (!this._track || this._pages.length < 2) return;

    this._track.addEventListener('scroll', this._onTrackScroll, { passive: true });
    this._dots.forEach((dot) => {
      dot.addEventListener('click', this._onDotClick);
    });
  }

  _onTrackScroll() {
    if (!this._track || !this._pages.length) return;
    const width = this._track.clientWidth;
    if (!width) return;

    const index = Math.round(this._track.scrollLeft / width);
    this._setActiveDot(index);
  }

  _onDotClick(event) {
    const index = Number(event.currentTarget.getAttribute('data-mm-specs-dot'));
    const page = this._pages[index];
    if (!page || !this._track) return;

    this._track.scrollTo({
      left: page.offsetLeft,
      behavior: MM_SPECS_REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
    this._setActiveDot(index);
  }

  _setActiveDot(index) {
    this._dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  _bindVideos() {
    this._videoCards.forEach((card) => {
      const inlineVideo = card.querySelector('.mm-specs__video');
      const button = card.querySelector('[data-mm-specs-play]');

      if (inlineVideo && button) {
        inlineVideo.pause();
        inlineVideo.loop = true;
        inlineVideo.playsInline = true;
        inlineVideo.setAttribute('playsinline', '');
        inlineVideo.setAttribute('webkit-playsinline', '');

        const sync = () => mmSpecsSyncPlayButton(button, inlineVideo);
        inlineVideo.addEventListener('play', sync);
        inlineVideo.addEventListener('pause', sync);
        inlineVideo.addEventListener('ended', sync);

        const onButtonClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (inlineVideo.paused) {
            const playPromise = inlineVideo.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => {
                inlineVideo.muted = true;
                inlineVideo.play().catch(() => {});
              });
            }
          } else {
            inlineVideo.pause();
          }
        };

        button.addEventListener('click', onButtonClick);
        sync();

        this._cleanups.push(() => {
          inlineVideo.removeEventListener('play', sync);
          inlineVideo.removeEventListener('pause', sync);
          inlineVideo.removeEventListener('ended', sync);
          button.removeEventListener('click', onButtonClick);
        });
      }

      const onCardClick = (event) => {
        if (event.target.closest('[data-mm-specs-play]')) return;
        event.preventDefault();
        this._openModal(card);
      };

      const onCardKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          if (event.target.closest('[data-mm-specs-play]')) return;
          event.preventDefault();
          this._openModal(card);
        }
      };

      card.addEventListener('click', onCardClick);
      card.addEventListener('keydown', onCardKeyDown);

      this._cleanups.push(() => {
        card.removeEventListener('click', onCardClick);
        card.removeEventListener('keydown', onCardKeyDown);
      });
    });
  }

  _bindModal() {
    if (!this._modal || !this._modalContent) return;

    const onClose = (event) => {
      event.preventDefault();
      this._closeModal();
    };

    this._modalCloses.forEach((closeBtn) => {
      closeBtn.addEventListener('click', onClose);
      this._cleanups.push(() => closeBtn.removeEventListener('click', onClose));
    });

    document.addEventListener('keydown', this._onDocumentKeyDown);
    this._cleanups.push(() => {
      document.removeEventListener('keydown', this._onDocumentKeyDown);
    });
  }

  _onDocumentKeyDown(event) {
    if (event.key === 'Escape' && this._modal && this._modal.classList.contains('is-open')) {
      this._closeModal();
    }
  }

  _openModal(card) {
    if (!this._modal || !this._modalContent) return;

    const inlineVideo = card.querySelector('.mm-specs__video');
    if (inlineVideo && !inlineVideo.paused) {
      inlineVideo.pause();
    }

    const template = card.querySelector('[data-mm-specs-modal-template]');
    if (!template) return;

    this._lastFocusedCard = card;
    this._modalContent.innerHTML = '';
    const content = template.content.cloneNode(true);
    this._modalContent.appendChild(content);

    const modalVideo = this._modalContent.querySelector('video');
    if (modalVideo) {
      modalVideo.currentTime = 0;
      modalVideo.muted = false;
      const playPromise = modalVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          modalVideo.muted = true;
          modalVideo.play().catch(() => {});
        });
      }
    }

    this._modal.classList.add('is-open');
    this._modal.setAttribute('aria-hidden', 'false');
    if (document.body) {
      document.body.classList.add('mm-specs-modal-open');
    }

    const closeBtn = this._modal.querySelector('.mm-specs__modal-close');
    if (closeBtn) {
      closeBtn.focus();
    }
  }

  _closeModal() {
    if (!this._modal || !this._modalContent) return;

    const modalVideo = this._modalContent.querySelector('video');
    if (modalVideo) {
      modalVideo.pause();
    }

    this._modalContent.innerHTML = '';
    this._modal.classList.remove('is-open');
    this._modal.setAttribute('aria-hidden', 'true');
    if (document.body) {
      document.body.classList.remove('mm-specs-modal-open');
    }

    if (this._lastFocusedCard && typeof this._lastFocusedCard.focus === 'function') {
      this._lastFocusedCard.focus();
      this._lastFocusedCard = null;
    }
  }
}

if (!customElements.get('mm-specs')) {
  customElements.define('mm-specs', MmSpecs);
}
